import {useCallback, useMemo, useRef, useState} from 'react';
import validateHelper from 'validate.js';
import {getPageQuery} from '~/utils/helpers';
import {useHistory} from 'react-router-dom';
import AppError from '~/utils/error-handler';
import {decryptUrl, hashUrl} from '~/utils/helpers/crypto';
import {isEmpty, unset} from 'lodash';
import {URL_FILTER_KEY} from '~/constants';

export interface ValidationErrors {
  [key: string]: string[];
}

export interface Rules extends Record<string, any> {}

interface ValidationOptions {
  rules: Rules;
}

interface BasicPageQueryOption {
  default: (v: any) => any;
  name: string;
}

interface PageQueryOptions extends Record<string, string | BasicPageQueryOption> {}

const parseUrlOpts = (pageQueryOpts: PageQueryOptions | undefined) => {
  if (!pageQueryOpts) {
    return [];
  }
  const pageQuery = getPageQuery();

  const sanitizeOpts: Record<string, BasicPageQueryOption> = {};
  for (const paramName in pageQueryOpts) {
    if (typeof pageQueryOpts[paramName] === 'string') {
      sanitizeOpts[paramName] = {
        default: v => JSON.parse(v),
        name: pageQueryOpts[paramName] as string,
      };
    } else {
      sanitizeOpts[paramName] = pageQueryOpts[paramName] as BasicPageQueryOption;
    }
  }

  const defaultQueryParamValues: Record<string, any> = {};
  const hashedFilterParams = pageQuery[URL_FILTER_KEY]?.toString() || '';
  const filterParamsStr = decryptUrl(hashedFilterParams);
  const filterObj = filterParamsStr ? JSON.parse(filterParamsStr) : {};

  for (const paramName in sanitizeOpts) {
    const key = sanitizeOpts[paramName].name;
    if (filterObj[key] !== undefined) {
      defaultQueryParamValues[paramName] = sanitizeOpts[paramName].default(filterObj[key]);
    }
  }

  return [sanitizeOpts, defaultQueryParamValues];
};

export function useInput<T extends Record<string, any>>(
  initialState: T = {} as T,
  validationOptions?: ValidationOptions,
  pageQueryOpts?: PageQueryOptions,
) {
  const pageQueryOptsRef = useRef<PageQueryOptions | undefined>(pageQueryOpts);

  const [sanitizeUrlOpts, defaultQueryParamValues] = useMemo(() => parseUrlOpts(pageQueryOptsRef.current), []);

  const defaultInputs = {
    ...initialState,
    ...defaultQueryParamValues,
  };
  const history = useHistory();
  const [inputs, setInputsLocal] = useState<T>(defaultInputs);
  const [errors, setErrors] = useState<ValidationErrors>();

  const syncParamsAndUrl = useCallback(
    (data: Record<string, any>) => {
      try {
        const pageQuery = getPageQuery() || {};
        const hashedFilterParams = pageQuery[URL_FILTER_KEY]?.toString() || '';
        const filterParamsStr = decryptUrl(hashedFilterParams);

        unset(pageQuery, URL_FILTER_KEY);

        const allUrlParams = new URLSearchParams(pageQuery as {[key: string]: string});
        const filterObj = filterParamsStr ? JSON.parse(filterParamsStr) : {};

        for (const key in data) {
          if (sanitizeUrlOpts[key]) {
            const queryValue = data[key] ? JSON.stringify(data[key]) : '';
            const queryKey = sanitizeUrlOpts[key].name;

            if (queryValue) {
              filterObj[queryKey] = queryValue;
            } else {
              unset(filterObj, queryKey);
            }
          }
        }

        const newFilterParamsHashed = !isEmpty(filterObj) ? hashUrl(JSON.stringify(filterObj)) : undefined;

        if (newFilterParamsHashed) {
          allUrlParams.set(URL_FILTER_KEY, newFilterParamsHashed);
        } else {
          allUrlParams.delete(URL_FILTER_KEY);
        }

        history.push({search: allUrlParams.toString()});
      } catch (e) {
        new AppError(e);
      }
    },
    [history, sanitizeUrlOpts],
  );

  const handleInputChange = useCallback(
    (data: Record<string, any> = {}) => {
      if (!data.page) {
        data.page = 1;
      }

      if (!data.page && !data.limit && !inputs.limit) {
        data.page = 1;
        data.limit = 30;
      }
      setInputsLocal(i => ({...i, ...data}));
      if (pageQueryOptsRef.current) {
        syncParamsAndUrl(data);
      }
    },
    [inputs.limit, syncParamsAndUrl],
  );

  const resetInput = () => {
    setInputsLocal(initialState);
  };

  const trimSpaces = (text?: string) => {
    if (!text) {
      return '';
    }
    return text.replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' ');
  };

  const trimSpacesOnChange = (text?: string) => {
    if (!text) {
      return '';
    }
    return text.replace(/^\s+|\s+$/g, ' ').replace(/\s+/g, ' ');
  };

  const getErrors = (): ValidationErrors | undefined => {
    const rules = validationOptions?.rules;
    if (!rules) {
      return undefined;
    }
    validateHelper.validators.presence.options = {allowEmpty: false};
    const validationErrors = validateHelper.validate(inputs, rules);
    setErrors(validationErrors);
    return validationErrors;
  };

  return {
    validation: {
      getErrors,
      errors,
      setErrors,
    },
    setInputs: handleInputChange,
    inputs,
    resetInput,
    trimSpaces,
    trimSpacesOnChange,
  };
}
